import { PopoverProps } from '@fluentui/react-components';
import { useMemo, useState } from 'react';

import { WizardStateComponentProps } from './wizard-state.model';
import WizardStateView from './wizard-state.view';

/**
 * Derives completed stage group names by comparing displayOrder values.
 * Stage groups with a displayOrder lower than the current group's are considered completed.
 */
const getCompletedStageGroups = (
  currentStageGroup: string,
  lifecycleStageGroups?: Record<
    string,
    { stages: string[]; displayActively: boolean; displayOrder: number }
  >
): string[] => {
  if (!lifecycleStageGroups || !currentStageGroup) return [];

  const currentGroup = lifecycleStageGroups[currentStageGroup];
  if (!currentGroup) return [];

  const currentOrder = currentGroup.displayOrder;

  return Object.entries(lifecycleStageGroups)
    .filter(([, group]) => group.displayOrder < currentOrder)
    .map(([groupName]) => groupName);
};

export const WizardStateController = (props: WizardStateComponentProps) => {
  const [open, setOpen] = useState(false);
  const handleOpenChange: PopoverProps['onOpenChange'] = (e, data) => {
    // check if the id's dropdown options and no result options are in view port then keep open the workflow history
    // else let event handle the selection
    const target = e.currentTarget as EventTarget & (HTMLElement | Document);

    if (target instanceof HTMLElement || target instanceof Document) {
      const dropdownWithOptions = target.querySelector(
        '#dropdown-with-options'
      );
      const dropdownWithoutOptions = target.querySelector(
        '#dropdown-with-no-results'
      );
      if (dropdownWithOptions || dropdownWithoutOptions) {
        setOpen(true);
      } else {
        setOpen(data.open || false);
      }
    } else {
      setOpen(data.open || false);
    }
  };

  // Derive completed stages from lifecycle displayOrder — no API call needed.
  // Stage groups with displayOrder < current group's displayOrder are completed.
  const taskGroupNames = useMemo(
    () =>
      getCompletedStageGroups(
        props.wizardState,
        props.objectDefinitions?.lifecycleStageGroups
      ),
    [props.wizardState, props.objectDefinitions?.lifecycleStageGroups]
  );

  return (
    <WizardStateView
      {...props}
      loading={false}
      handleOpenChange={handleOpenChange}
      open={open}
      taskGroupNames={taskGroupNames}
    />
  );
};
